/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { type FunctionComponent, useState, useEffect, type JSX } from "react";
import { DateTime } from "@tivocorp/data-domain-lib";

import type { ProgressBarProps } from "./ProgressBarProps";

import "./ProgressBar.scss";

const computeVisibleProgressPercent = (
  startTime: DateTime,
  durationSeconds: number
): { progress: number; isVisible: boolean } => {
  const now = new DateTime();
  const total = durationSeconds * 1000;
  const elapsed = now.getTime() - startTime.getTime();
  const isVisible = elapsed >= 0 && elapsed <= total;
  const clamped = Math.max(0, Math.min(elapsed, total));
  const progress = total > 0 ? (clamped / total) * 100 : 0;
  return { progress, isVisible };
};

/**
 * ProgressBar component displays a progress bar based on start time, duration, and current time.
 * @param props.startTime        - The start time of the progress.
 * @param props.durationSeconds  - The duration of the progress in seconds.
 * @param props.classStyle       - For customization of the progress bar's CSS class.
 * @param props.refreshIntervalMs - Interval in ms to auto-refresh progress. Null disables auto-update.
 */
const ProgressBar: FunctionComponent<ProgressBarProps> = ({
  startTime,
  durationSeconds,
  classStyle = "",
  refreshIntervalMs = null
}): JSX.Element => {
  const TAG: string = "ProgressBar";

  // Trigger re-renders on interval ticks; progress is derived each render from props + current time
  const [, setTick] = useState<number>(0);

  const { progress: progressPercent, isVisible } =
    computeVisibleProgressPercent(startTime, durationSeconds);

  // Set up auto-refresh interval only when refreshIntervalMs is provided
  useEffect(() => {
    if (
      refreshIntervalMs === null ||
      refreshIntervalMs === undefined ||
      !isVisible
    ) {
      return;
    }

    const intervalId = setInterval((): void => {
      setTick((prev: number) => prev + 1);
    }, refreshIntervalMs);

    return (): void => {
      clearInterval(intervalId);
    };
  }, [refreshIntervalMs, isVisible]);

  const baseClass: string =
    "progress-bar" + (classStyle !== "" ? " " + classStyle : "");

  if (!isVisible) {
    return <></>;
  }

  return (
    <div className={baseClass} data-tag={TAG}>
      <div
        className="progress-bar__fill"
        style={{ width: `${progressPercent}%` }}
      ></div>
    </div>
  );
};

export default ProgressBar;
